<script setup>
import { storeToRefs } from "pinia";
import { computed, onMounted, onBeforeMount } from "vue";
import AgentLayout from "./layouts/AgentLayout.vue";
// import PageHeader from "@/components/shared/PageHeader.vue";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useRouter } from "vue-router";
import { ref } from "vue";
import { useStatisticsStore } from "@/stores/statistics";
import { useCompanyStore } from "@/stores/company";
import { useDataCollectionStore } from "@/stores/dataCollection";
import { useAgentsStore } from "@/stores/agents";
import { useDevicesStore } from "@/stores/devices";
const { companyId } = storeToRefs(useCompanyStore());
const router = useRouter();
const statisticsStore = useStatisticsStore();
const dataCollectionStore = useDataCollectionStore();
const agentsStore = useAgentsStore();
const devicesStore = useDevicesStore();

const setupCompletion = ref(0);
const hasDataSources = ref(false);
const hasAgents = ref(false);
const hasDevices = ref(false);

// const totalConversations = ref(0);
// const avgPerDay = ref(0);

const navigateTo = (path) => {
  if (path) {
    router.push(path);
  }
};

const highlightSidebar = (id) => {
  // Elevate the entire sidebar z-index to sit above the driver overlay
  const sidebar = document.querySelector(".sidebar");
  if (sidebar) {
    sidebar.style.zIndex = "1000000002";
  }

  // Optional: Scroll the item into view if needed
  const item = document.querySelector(`.sidebar-item-${id}`);
  if (item) {
    item.classList.add("tour-active");
    item.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};

const unhighlightSidebar = (id) => {
  const sidebar = document.querySelector(".sidebar");
  if (sidebar) {
    sidebar.style.zIndex = "";
  }

  if (id) {
    const item = document.querySelector(`.sidebar-item-${id}`);
    if (item) {
      item.classList.remove("tour-active");
    }
  }
};

const driverObj = driver({
  showProgress: true,
  allowClose: true,
  steps: [
    {
      popover: {
        title: "Welcome to RetailHub",
        description:
          "Manage your retail operations and AI agents from one central dashboard. Let's take a tour!",
        align: "center",
      },
    },
    {
      element: ".tour-setup-completion",
      popover: {
        title: "Setup Completion",
        description:
          "Track your progress in setting up your retail operations and AI agents.",
      },
    },
    {
      element: ".tour-quick-actions",
      popover: {
        title: "Quick Actions",
        description:
          "Access frequently used actions like adding sources or creating new agents.",
      },
    },
    {
      element: ".tour-step-0",
      popover: {
        title: "Data Library",
        description:
          "Start here by organizing your business assets and training materials.",
      },
      onHighlightStarted: () => highlightSidebar(4),
      onDeselected: () => unhighlightSidebar(4),
    },
    {
      element: ".tour-step-1",
      popover: {
        title: "Data Source",
        description:
          "Connect your external feeds and live inventories to power your agents.",
        side: "right",
        align: "start",
      },
      onHighlightStarted: () => highlightSidebar(4),
      onDeselected: () => unhighlightSidebar(4),
    },
    {
      element: ".tour-step-2",
      popover: {
        title: "Agent Configuration",
        description:
          "Configure your AI behaviors, response logic, and personality.",
      },
      onHighlightStarted: () => highlightSidebar(5),
      onDeselected: () => unhighlightSidebar(5),
    },
    {
      element: ".tour-step-3",
      popover: {
        title: "Device Connection",
        description:
          "Sync hardware sensors and point-of-sale systems with your platform.",
      },
      onHighlightStarted: () => highlightSidebar(3),
      onDeselected: () => unhighlightSidebar(3),
    },
    {
      element: ".tour-library-stats",
      popover: {
        title: "Data Library Stats",
        description:
          "View statistics about your files, websites, and integrations at a glance.",
      },
    },
    {
      element: ".tour-conversation-stats",
      popover: {
        title: "Conversation Stats",
        description:
          "Monitor total conversations, response rates, and average response times.",
      },
    },
    {
      popover: {
        title: "You're All Set!",
        description:
          "You are now ready to start using RetailHub. Click the button below to start by adding knowledge to your Data Library.",
        align: "center",
      },
    },
  ],
  doneBtnText: "Finish",
});

const startTour = () => {
  driverObj.drive();
};

onMounted(() => {
});

const quickActions = [
  {
    label: "Add Source",
    icon: "bi bi-plus-circle-fill",
    route: "/data-source/create",
  },
  {
    label: "New Agent",
    icon: "bi bi-person-plus-fill",
    route: "/agents-management",
  },
  // { label: "View Logs", icon: "bi bi-file-text-fill", route: "#" },
];

const requiredSteps = [
  {
    title: "Data Library",
    description: "Upload files, save favorite websites, and sync with retail stores. All your data in one smart, easy hub.",
    icon: "bi bi-folder-fill",
    actionText: "Set Up",
    active: true,
    route: "/data-library",
  },
  {
    title: "Data Source",
    description: "Group files, sites, and stores into one source to easily power your agent with all your data at once.",
    icon: "bi bi-cloud-upload-fill",
    actionText: "Add Source",
    active: false,
    route: "/data-source/create",
  },
  {
    title: "Agent",
    description: "Manage your agents, set their type and status, and link multiple data sources to assign them to any device.",
    icon: "bi bi-robot",
    actionText: "Create Agent",
    active: false,
    route: "/agents-management",
  },
  {
    title: "Device",
    description: "Assign your custom agents to any device to sync data, track status, and power up your hardware instantly.",
    icon: "bi bi-tablet",
    actionText: "Connect",
    active: false,
    route: "/sites-devices",
  },
];

const libraryStats = ref([
  { label: "Files", value: "0", sub: "" },
  { label: "Websites", value: "0", sub: "" },
  { label: "Integrations", value: "0", sub: "" },
]);

// Data Library is verified against its own vitals card (fetched below);
// Data Source / Agent / Device are verified against a lightweight fetch each.
// Once any of a step's counts is non-zero it's done. Only the earliest
// not-yet-done step is flagged "Start here" — later ones stay neutral until
// it's their turn, same as a sequential checklist.
const isLibrarySetUp = computed(() =>
  libraryStats.value.some((stat) => Number(stat.value) > 0)
);

const stepDoneFlags = computed(() => [
  isLibrarySetUp.value,
  hasDataSources.value,
  hasAgents.value,
  hasDevices.value,
]);
const nextStepIndex = computed(() => stepDoneFlags.value.findIndex((done) => !done));

const isStepDone = (index) => stepDoneFlags.value[index];
const isStepActive = (index) => index === nextStepIndex.value;

const conversationStats = ref([
  { label: "Total Conversations", value: "0", sub: "" },
  { label: "Avg. per Day", value: "0", sub: "" },
]);

const fetchConversationsStats = async () => {
  try {
    const res = await statisticsStore.fetchConversationsStats({ companyId: companyId.value, params: { period: 'daily' } });
    conversationStats.value = [
      { label: "Total Conversations", value: res.total_conversations, sub: "" },
      { label: "Avg. per Day", value: Number(res.average_conversations).toFixed(2), sub: "" },
    ];
  } catch { /* store already shows a toast */ }
}

const fetchCompanySetupProgress = async () => {
  try {
    const res = await statisticsStore.fetchCompanySetupProgress(companyId.value);
    setupCompletion.value = res.data.setup_complete;
  } catch { /* store already shows a toast */ }
}

const fetchCompanyOverviewStatistics = async () => {
  try {
    const res = await statisticsStore.fetchCompanyOverviewStatistics(companyId.value);
    libraryStats.value = [
      { label: "Files", value: res.data.files, sub: "" },
      { label: "Websites", value: res.data.urls, sub: "" },
      { label: "Integrations", value: res.data.integrations, sub: "" },
    ];
  } catch { /* store already shows a toast */ }
}

const fetchDataSourcesStatus = async () => {
  try {
    const res = await dataCollectionStore.fetchDataCollectionsSimple(companyId.value);
    hasDataSources.value = (res.data?.length || 0) > 0;
  } catch { /* store already shows a toast */ }
}

const fetchAgentsStatus = async () => {
  try {
    const res = await agentsStore.fetchAgentsSimple(companyId.value);
    hasAgents.value = (res.data?.length || 0) > 0;
  } catch { /* store already shows a toast */ }
}

const fetchDevicesStatus = async () => {
  try {
    const res = await devicesStore.fetchAllDevices(companyId.value);
    hasDevices.value = (res.data?.length || 0) > 0;
  } catch { /* store already shows a toast */ }
}

onBeforeMount(() => {
  fetchConversationsStats();
  fetchCompanyOverviewStatistics();
  fetchCompanySetupProgress();
  fetchDataSourcesStatus();
  fetchAgentsStatus();
  fetchDevicesStatus();
});
  
</script>

<template>
  <AgentLayout full-width>
    <div class="an-page ov-page">
      <!-- ── Page header ───────────────────────────────────────── -->
      <header class="ov-head">
        <div class="ov-head__title">
          <h2>Overview</h2>
          <span class="ov-head__sub">Your retail operations and AI agents at a glance</span>
        </div>
        <span class="ov-head__live"><i class="ov-head__dot"></i>Live</span>
      </header>

      <div class="ov-deck">
        <!-- ── Hero ──────────────────────────────────────────── -->
        <section class="an-panel ov-hero">
          <div class="ov-hero__intro">
            <span class="ov-eyebrow">Ravo Command Center</span>
            <h1 class="ov-hero__title">Welcome back</h1>
            <p class="ov-hero__sub">
              Manage your retail operations and AI agents from one
              central dashboard.
            </p>
            <button type="button" class="ov-tour" @click="startTour">
              <i class="bi bi-compass"></i>
              Take a tour
            </button>
          </div>

          <div class="ov-hero__ring tour-setup-completion">
            <div
              class="ov-ring"
              :style="{ '--p': setupCompletion }"
              role="progressbar"
              :aria-valuenow="setupCompletion"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-label="Setup completion"
            >
              <div class="ov-ring__inner">
                <b>{{ setupCompletion }}<small>%</small></b>
                <span>setup</span>
              </div>
            </div>
            <span class="ov-ring__hint">
              {{ setupCompletion >= 100 ? "You're all set" : "Finish the steps below" }}
            </span>
          </div>
        </section>

        <!-- ── Quick actions ─────────────────────────────────── -->
        <section class="an-panel ov-quick tour-quick-actions">
          <div class="ov-cardhead"><span class="ov-cardhead__t">Quick actions</span></div>
          <div class="ov-pills">
            <button
              v-for="(action, index) in quickActions"
              :key="index"
              type="button"
              class="ov-pill"
              @click="navigateTo(action.route)"
            >
              <i :class="action.icon"></i>
              <span>{{ action.label }}</span>
              <i class="bi bi-arrow-right-short ov-pill__go"></i>
            </button>
          </div>
        </section>

        <!-- ── Data Library vitals ───────────────────────────── -->
        <section class="an-panel ov-vital ov-vital--lib tour-library-stats">
          <div class="ov-cardhead">
            <span class="ov-cardhead__t">Data Library</span>
            <router-link to="/data-library" class="ov-more">
              Open <i class="bi bi-arrow-right-short"></i>
            </router-link>
          </div>
          <div class="ov-metrics">
            <div v-for="(stat, index) in libraryStats" :key="index" class="ov-metric">
              <span class="ov-metric__value">{{ stat.value }}</span>
              <span class="ov-metric__label">{{ stat.label }}</span>
            </div>
          </div>
        </section>

        <!-- ── Conversation vitals ───────────────────────────── -->
        <section class="an-panel ov-vital ov-vital--conv tour-conversation-stats">
          <div class="ov-cardhead">
            <span class="ov-cardhead__t">Conversations</span>
            <router-link to="/conversations-analytics" class="ov-more">
              Analytics <i class="bi bi-arrow-right-short"></i>
            </router-link>
          </div>
          <div class="ov-metrics">
            <div v-for="(stat, index) in conversationStats" :key="index" class="ov-metric">
              <span class="ov-metric__value">{{ stat.value }}</span>
              <span class="ov-metric__label">{{ stat.label }}</span>
            </div>
          </div>
        </section>

        <!-- ── Setup pipeline ────────────────────────────────── -->
        <div class="ov-railhead">
          <h3>Required setup steps</h3>
          <small>Follow the sequence to bring an agent online</small>
        </div>

        <article
          v-for="(step, index) in requiredSteps"
          :key="index"
          class="ov-step an-panel"
          :class="['tour-step-' + index, { 'is-active': isStepActive(index), 'is-done': isStepDone(index) }]"
        >
          <header class="ov-step__head">
            <span class="ov-step__num">{{ String(index + 1).padStart(2, "0") }}</span>
            <span class="ov-step__icon"><i :class="isStepDone(index) ? 'bi bi-check-lg' : step.icon"></i></span>
            <span v-if="isStepActive(index)" class="ov-step__flag">{{ index === 0 ? "Start here" : "Up next" }}</span>
            <span v-else-if="isStepDone(index)" class="ov-step__flag ov-step__flag--done">Completed</span>
          </header>
          <h4 class="ov-step__title">{{ step.title }}</h4>
          <p class="ov-step__desc">{{ step.description }}</p>
          <button
            type="button"
            class="ov-step__cta"
            :class="{ 'is-primary': isStepActive(index) }"
            @click="navigateTo(step.route)"
          >
            {{ step.actionText }}
            <i class="bi bi-arrow-right-short"></i>
          </button>
        </article>
      </div>
    </div>
  </AgentLayout>
</template>

<style scoped>
/* ===========================================================
   Overview — compact "control deck". A single 12-col bento
   grid: hero + quick actions + both vitals share the top two
   rows, the setup pipeline sits below — so the whole landing
   reads in roughly one screen plus a short scroll. Built on the
   analytics console (.an-*) tokens, themes light/dark for free.
   =========================================================== */
.ov-page { font-family: var(--an-font-data); }

/* ── Page header ──────────────────────────────────────────── */
.ov-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding-bottom: 16px;
  margin-bottom: 18px;
  border-bottom: 1px solid var(--an-line);
}
.ov-head__title { display: flex; flex-direction: column; gap: 4px; }
.ov-head__title h2 { font-size: 20px; font-weight: 800; letter-spacing: -0.01em; color: var(--an-text); margin: 0; }
.ov-head__sub { font-size: 12px; color: var(--an-muted); }
.ov-head__live {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--an-muted);
}
.ov-head__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--an-good);
  box-shadow: 0 0 8px var(--an-good);
}

.ov-deck {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 14px;
  align-items: stretch;
}
/* every panel is a flush, compact card on the deck */
.ov-deck > .an-panel { padding: 16px 18px; }

.ov-eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--an-accent);
}

/* ── Hero (span 8) ────────────────────────────────────────── */
.ov-hero {
  grid-column: span 8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}
.ov-hero::after {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(440px 220px at 100% -30%, rgba(56, 189, 248, 0.12), transparent 60%);
  pointer-events: none;
}
.ov-hero__intro { position: relative; display: flex; flex-direction: column; gap: 6px; }
.ov-hero__title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--an-text);
  margin: 1px 0 0;
  text-wrap: balance;
}
.ov-hero__sub { font-size: 12.5px; color: var(--an-muted); max-width: 42ch; margin: 0; line-height: 1.5; }
.ov-tour {
  margin-top: 10px;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  font-weight: 600;
  color: var(--an-text);
  background: var(--an-track);
  border: 1px solid var(--an-line);
  border-radius: 9px;
  padding: 8px 13px;
  cursor: pointer;
  transition: border-color 0.18s ease, transform 0.18s ease;
}
.ov-tour:hover { border-color: var(--an-accent); transform: translateY(-1px); }
.ov-tour i { color: var(--an-accent); font-size: 13px; }

/* completion ring */
.ov-hero__ring { position: relative; display: flex; flex-direction: column; align-items: center; gap: 9px; flex: none; }
.ov-ring {
  --p: 0;
  width: 104px;
  height: 104px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background:
    conic-gradient(
      var(--an-good) calc(var(--p) * 1%),
      var(--an-track) calc(var(--p) * 1%)
    );
  position: relative;
}
.ov-ring::before {
  content: "";
  position: absolute;
  inset: 9px;
  border-radius: 50%;
  background: var(--an-panel);
  border: 1px solid var(--an-line);
}
.ov-ring__inner { position: relative; text-align: center; line-height: 1; }
.ov-ring__inner b {
  display: block;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--an-text);
}
.ov-ring__inner b small { font-size: 14px; color: var(--an-muted); font-weight: 600; }
.ov-ring__inner span {
  font-size: 8.5px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--an-muted);
}
.ov-ring__hint { font-size: 11.5px; color: var(--an-text); font-weight: 600; text-align: center; }

/* ── Card head (shared by quick + vitals) ─────────────────── */
.ov-cardhead { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px; }
.ov-cardhead__t {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--an-text);
}
.ov-more {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  font-size: 11px;
  font-weight: 600;
  color: var(--an-accent);
  text-decoration: none;
}
.ov-more i { font-size: 15px; transition: transform 0.18s ease; }
.ov-more:hover i { transform: translateX(2px); }

/* ── Quick actions (span 4) ───────────────────────────────── */
.ov-quick { grid-column: span 4; display: flex; flex-direction: column; }
.ov-pills { display: flex; flex-direction: column; gap: 8px; }
.ov-pill {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--an-text);
  background: var(--an-track);
  border: 1px solid var(--an-line);
  border-radius: 9px;
  padding: 9px 12px;
  cursor: pointer;
  transition: border-color 0.18s ease, transform 0.18s ease;
}
.ov-pill:hover { border-color: var(--an-accent); transform: translateY(-1px); }
.ov-pill > i:first-child { color: var(--an-accent); font-size: 14px; }
.ov-pill span { flex: 1; text-align: left; }
.ov-pill__go { color: var(--an-faint); font-size: 17px; transition: transform 0.18s ease; }
.ov-pill:hover .ov-pill__go { transform: translateX(2px); color: var(--an-accent); }

/* ── Vitals (lib span 7 / conv span 5) ────────────────────── */
.ov-vital { display: flex; flex-direction: column; }
.ov-vital--lib { grid-column: span 7; }
.ov-vital--conv { grid-column: span 5; }
.ov-metrics { display: flex; gap: 10px; flex: 1; }
.ov-metric {
  flex: 1;
  min-width: 0;
  background: var(--an-track);
  border: 1px solid var(--an-line);
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
}
.ov-metric__value { font-size: 26px; font-weight: 700; letter-spacing: -0.02em; color: var(--an-text); font-variant-numeric: tabular-nums; line-height: 1; }
.ov-metric__label { font-size: 10px; font-weight: 600; letter-spacing: 0.07em; text-transform: uppercase; color: var(--an-muted); }

/* ── Pipeline header (span 12) ────────────────────────────── */
.ov-railhead { grid-column: span 12; display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; margin-top: 6px; }
.ov-railhead h3 {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--an-text);
  margin: 0;
}
.ov-railhead small { font-size: 11px; color: var(--an-faint); }

/* ── Setup steps (span 3 each) ────────────────────────────── */
.ov-step {
  grid-column: span 3;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: border-color 0.18s ease, transform 0.18s ease;
}
.ov-step:hover { transform: translateY(-2px); border-color: var(--an-accent); }
.ov-step.is-active::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: linear-gradient(var(--an-accent), transparent);
}
.ov-step.is-active { border-color: rgba(56, 189, 248, 0.4); }
.ov-step.is-done::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: linear-gradient(var(--an-good), transparent);
}
.ov-step.is-done { border-color: color-mix(in srgb, var(--an-good) 40%, transparent); }
.ov-step.is-done .ov-step__icon { background: color-mix(in srgb, var(--an-good) 14%, transparent); color: var(--an-good); }
.ov-step__head { display: flex; align-items: center; gap: 9px; }
.ov-step__num {
  font-size: 12px;
  font-weight: 700;
  color: var(--an-faint);
  font-variant-numeric: tabular-nums;
}
.ov-step__icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: var(--an-track);
  color: var(--an-accent);
  font-size: 14px;
}
.ov-step.is-active .ov-step__icon { background: rgba(56, 189, 248, 0.14); }
.ov-step__flag {
  margin-left: auto;
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--an-accent);
  background: rgba(56, 189, 248, 0.12);
  padding: 3px 7px;
  border-radius: 6px;
}
.ov-step__flag--done { color: var(--an-good); background: color-mix(in srgb, var(--an-good) 12%, transparent); }
.ov-step__title { font-size: 14px; font-weight: 700; color: var(--an-text); margin: 2px 0 0; }
.ov-step__desc {
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--an-muted);
  margin: 0;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.ov-step__cta {
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: var(--an-text);
  background: var(--an-track);
  border: 1px solid var(--an-line);
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}
.ov-step__cta:hover { border-color: var(--an-accent); }
.ov-step__cta.is-primary { background: var(--an-accent); border-color: var(--an-accent); color: #06121f; }
.ov-step__cta.is-primary:hover { filter: brightness(1.06); }
.ov-step__cta i { font-size: 15px; }

/* ── Responsive ───────────────────────────────────────────── */
@media (max-width: 1100px) {
  .ov-hero { grid-column: span 12; }
  .ov-quick { grid-column: span 12; }
  .ov-quick .ov-pills { flex-direction: row; flex-wrap: wrap; }
  .ov-quick .ov-pill { flex: 1; min-width: 160px; }
  .ov-vital--lib, .ov-vital--conv { grid-column: span 6; }
  .ov-step { grid-column: span 6; }
}
@media (max-width: 720px) {
  .ov-hero { flex-direction: column; align-items: flex-start; gap: 18px; }
  .ov-hero__ring { flex-direction: row; align-self: stretch; justify-content: flex-start; }
  .ov-vital--lib, .ov-vital--conv { grid-column: span 12; }
  .ov-step { grid-column: span 12; }
}

@media (prefers-reduced-motion: reduce) {
  .ov-step, .ov-pill, .ov-tour, .ov-pill__go, .ov-more i { transition: none; }
}
</style>
